import { Controller } from "@hotwired/stimulus";
import { patch } from "@rails/request.js";

const DEFAULT_CELL_SIZE = 80;

/**
 * Handles resizing of tables on the floor plan grid
 */
export default class extends Controller {
  static values = {
    tableId: Number,
    gridCols: { type: Number, default: 12 },
    gridRows: { type: Number, default: 8 },
  };

  connect() {
    this.resizing = false;
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundEndResize = this.endResize.bind(this);
  }

  get cellSize() {
    const grid = this.element.closest("[data-floor-plan-target='grid']");
    if (grid && grid.dataset.cellSize) {
      return parseFloat(grid.dataset.cellSize);
    }
    return DEFAULT_CELL_SIZE;
  }

  disconnect() {
    document.removeEventListener("mousemove", this.boundHandleResize);
    document.removeEventListener("mouseup", this.boundEndResize);
  }

  start(event) {
    event.preventDefault();
    event.stopPropagation();

    const tableElement = this.element;
    const currentWidth = parseInt(tableElement.dataset.gridWidth) || 1;
    const currentHeight = parseInt(tableElement.dataset.gridHeight) || 1;

    this.resizing = {
      element: tableElement,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: currentWidth,
      startHeight: currentHeight,
      currentWidth: currentWidth,
      currentHeight: currentHeight,
    };

    document.addEventListener("mousemove", this.boundHandleResize);
    document.addEventListener("mouseup", this.boundEndResize);
  }

  handleResize(event) {
    if (!this.resizing) return;

    const deltaX = event.clientX - this.resizing.startX;
    const deltaY = event.clientY - this.resizing.startY;

    const newWidth = Math.max(
      1,
      Math.min(
        this.gridColsValue,
        this.resizing.startWidth + Math.round(deltaX / this.cellSize)
      )
    );
    const newHeight = Math.max(
      1,
      Math.min(
        this.gridRowsValue,
        this.resizing.startHeight + Math.round(deltaY / this.cellSize)
      )
    );

    // Only update if changed
    if (
      newWidth !== this.resizing.currentWidth ||
      newHeight !== this.resizing.currentHeight
    ) {
      const width = newWidth * this.cellSize - 8;
      const height = newHeight * this.cellSize - 8;

      this.resizing.element.style.width = `${width}px`;
      this.resizing.element.style.height = `${height}px`;
      this.resizing.currentWidth = newWidth;
      this.resizing.currentHeight = newHeight;
    }
  }

  endResize(event) {
    document.removeEventListener("mousemove", this.boundHandleResize);
    document.removeEventListener("mouseup", this.boundEndResize);

    if (!this.resizing) return;

    const { startWidth, startHeight, currentWidth, currentHeight } =
      this.resizing;

    if (currentWidth !== startWidth || currentHeight !== startHeight) {
      // Update data attributes
      this.element.dataset.gridWidth = currentWidth;
      this.element.dataset.gridHeight = currentHeight;

      this.saveSize(currentWidth, currentHeight);
    }

    this.resizing = null;
  }

  async saveSize(gridWidth, gridHeight) {
    const venueId = this.getVenueIdFromUrl();
    const url = `/venues/${venueId}/tables/${this.tableIdValue}/position`;

    try {
      const response = await patch(url, {
        body: {
          admin_venues_tables_positions_update_form: {
            grid_width: gridWidth,
            grid_height: gridHeight,
          },
        },
      });

      if (!response.ok) {
        console.error("Failed to update table size");
        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating table size:", error);
      window.location.reload();
    }
  }

  getVenueIdFromUrl() {
    const pathParts = window.location.pathname.split("/");
    const venueIndex = pathParts.indexOf("venues");
    return pathParts[venueIndex + 1];
  }
}
